import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  StackSimple, GearSix, SignOut, Key, House, Flask,
  Planet, MapTrifold, Waveform, ShieldCheck,
} from "@phosphor-icons/react";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { StorageMeter } from "@/components/StorageMeter";
import GeoPiButton from "@/components/GeoPiButton";

const NAV = [
  { to: "/console", label: "Console", icon: House, testid: "nav-modes", exact: true },
  { to: "/projects", label: "Projects", icon: StackSimple, testid: "nav-projects" },
  { to: "/commodities", label: "Model Explorer", icon: Flask, testid: "nav-explorer" },
  { to: "/admin", label: "Admin", icon: GearSix, testid: "nav-admin", perm: "manage_users" },
];

export default function Layout({ children }) {
  const { user, logout, hasPerm } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();

  const p = loc.pathname;
  const MOD = p.startsWith("/remote-sensing") ? { label: "Remote Sensing", Icon: Planet }
    : p.startsWith("/maps") ? { label: "Geomapping", Icon: MapTrifold }
    : p.startsWith("/geophysics") ? { label: "Geophysics", Icon: Waveform }
    : p.startsWith("/admin") ? { label: "Administration", Icon: GearSix }
    : p.startsWith("/commodities") ? { label: "Model Explorer", Icon: Flask }
    : p.startsWith("/projects") ? { label: "Projects", Icon: StackSimple }
    : { label: "Console", Icon: House };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className="w-[68px] hover:w-56 group transition-all duration-200 border-r border-slate-200 bg-white flex flex-col fixed h-screen z-[1000] overflow-hidden">
        <div className="h-16 flex items-center gap-3 px-4 border-b border-slate-200">
          <div className="w-9 h-9 rounded-md bg-white border border-slate-200 flex items-center justify-center shrink-0 p-0.5 overflow-hidden">
            <img src="/symin-favicon-mark.png" alt="SYMIN" className="h-full w-full object-contain" />
          </div>
          <div className="opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
            <div className="font-display font-bold text-base leading-none tracking-tight text-slate-900">SYMIN</div>
          </div>
        </div>
        <nav className="flex-1 py-3 flex flex-col gap-1 px-2">
          {NAV.filter((n) => !n.perm || hasPerm(n.perm)).map((n) => {
            const active = n.exact ? loc.pathname === n.to : loc.pathname.startsWith(n.to);
            const Icon = n.icon;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={`flex items-center gap-3 h-10 px-3 rounded-md text-sm transition-colors ${
                  active ? "bg-primary text-primary-foreground" : "text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                }`}
              >
                <Icon size={20} weight={active ? "fill" : "regular"} className="shrink-0" />
                <span className="opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">{n.label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="p-2 border-t border-slate-200">
          <ChangePasswordDialog trigger={
            <button data-testid="sidebar-change-password" className="flex items-center gap-3 h-10 px-3 rounded-md text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-900 w-full">
              <Key size={20} />
              <span className="opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">Change password</span>
            </button>
          } />
          <button
            onClick={async () => { await logout(); nav("/login"); }}
            data-testid="logout-btn"
            className="flex items-center gap-3 h-10 px-3 rounded-md text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-900 w-full"
          >
            <SignOut size={20} />
            <span className="opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">Sign out</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 ml-[68px] flex flex-col min-w-0">
        <header className="relative h-[68px] bg-white shadow-[0_1px_4px_rgba(15,23,42,0.07)] flex items-center justify-between gap-3 px-4 sm:px-6 sticky top-0 z-[900]" data-testid="app-header">
          {/* left: logo + module indicator */}
          <div className="flex items-center gap-4 min-w-0">
            <Link to="/console" data-testid="header-logo">
              <img src="/symin-logo.png" alt="SYMIN" className="h-8 w-auto object-contain" />
            </Link>
            <div className="hidden sm:flex items-center gap-2.5 pl-4 border-l border-slate-200" data-testid="header-module">
              <span className="h-9 w-9 rounded-lg bg-[#C20511]/10 grid place-items-center shrink-0">
                <MOD.Icon size={18} weight="bold" className="text-[#C20511]" />
              </span>
              <div className="leading-tight">
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Module</div>
                <div className="text-sm font-bold text-slate-900 -mt-0.5">{MOD.label}</div>
              </div>
            </div>
          </div>

          {/* right: storage · user pill · icon buttons */}
          <div className="flex items-center gap-2 sm:gap-3 shrink-0" data-testid="app-header-right">
            <GeoPiButton className="hidden md:inline-flex" />
            <div className="hidden lg:block text-[11px] text-slate-400 font-mono"><StorageMeter /></div>
            <div className="flex items-center gap-2 rounded-full bg-slate-50 border border-slate-200 pl-2.5 pr-3 py-1.5" data-testid="header-user-pill">
              <ShieldCheck size={17} weight="fill" className="text-[#C20511] shrink-0" />
              <div className="leading-tight">
                <div className="text-[13px] font-semibold text-slate-900 whitespace-nowrap max-w-[120px] truncate">{user?.name}</div>
                <div className="hidden sm:block text-[10px] text-slate-500 -mt-0.5">{user?.role_label}</div>
              </div>
            </div>
            <ChangePasswordDialog trigger={
              <button data-testid="header-change-password" title="Change password"
                className="h-9 w-9 rounded-lg grid place-items-center text-slate-400 hover:text-[#C20511] hover:bg-slate-100 transition-colors">
                <Key size={18} />
              </button>
            } />
            <button onClick={async () => { await logout(); nav("/login"); }} data-testid="header-logout" title="Sign out"
              className="h-9 w-9 rounded-lg grid place-items-center text-slate-400 hover:text-[#C20511] hover:bg-slate-100 transition-colors">
              <SignOut size={18} />
            </button>
          </div>

          {/* thin red accent line */}
          <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#C20511]" />
        </header>
        <main className="flex-1 min-w-0">{children}</main>
      </div>
    </div>
  );
}
